import * as Tabs from '@radix-ui/react-tabs'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { motion } from 'framer-motion'
import {
  type ReactNode,
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FramerMotionTransition } from '@opallabs/gem'
import { useTheme, useUserEnvironment } from '@opallabs/hooks'
import { Button } from '../Button'
import { Body, type BodyProps } from './Body'
import { Footer } from './Footer'
import * as styles from './styles'

type MultiStepStep = Readonly<{
  bodyContent: ReactNode
  title: string
  nextDisabled?: boolean
}>
const useMultiStepState = ({
  stepCount,
  steps,
}: {
  stepCount: number
  steps: MultiStepStep[]
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const [stepHeights, setStepHeights] = useState<number[]>([])

  const measureStepHeights = useCallback(() => {
    const heights = stepRefs.current.map((ref) => ref?.scrollHeight ?? 0)
    setStepHeights(heights)
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => measureStepHeights())
    const resizeObserver = new ResizeObserver(measureStepHeights)

    const observerTimeoutId = setTimeout(() => {
      stepRefs.current.forEach((ref) => {
        if (ref) resizeObserver.observe(ref)
      })
    })

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(observerTimeoutId)
      resizeObserver.disconnect()
    }
  }, [measureStepHeights, steps])

  const navigateToStep = useCallback(
    (newStep: number) => {
      if (newStep >= 0 && newStep < stepCount) {
        setIsAnimating(true)
        setCurrentStep(newStep)
      }
    },
    [stepCount]
  )

  const handleTabValueChange = useCallback((value: string) => {
    const stepIndex = parseInt(value, 10)
    if (!Number.isNaN(stepIndex)) setCurrentStep(stepIndex)
  }, [])

  const currentStepHeight = useMemo(
    () => stepHeights[currentStep] ?? 0,
    [currentStep, stepHeights]
  )

  return {
    currentStep,
    currentStepHeight,
    handleTabValueChange,
    isAnimating,
    navigateToStep,
    setIsAnimating,
    stepRefs,
  }
}

type StepProgressBarProps = Readonly<{
  currentStep: number
  stepCount: number
  steps: { title: string }[]
}>
const StepProgressBar: React.FC<StepProgressBarProps> = ({
  currentStep,
  stepCount,
  steps,
}) => (
  <StrictMode>
    <Tabs.List className={styles.classes.progressBar}>
      {steps.map(({ title }, index) => (
        <Tabs.Trigger
          aria-label={`Step ${index + 1} of ${stepCount}`}
          className={
            index < currentStep
              ? 'is-previous'
              : index > currentStep
                ? 'is-future'
                : undefined
          }
          key={index}
          value={index.toString()}
        >
          <span className="sr-only">{title}</span>
        </Tabs.Trigger>
      ))}
    </Tabs.List>
  </StrictMode>
)
StepProgressBar.displayName = 'Lapidary: StepProgressBar ← MultiStep ← Modal'

type StepCarouselProps = Readonly<{
  bodyProps: BodyProps
  currentStep: number
  currentStepHeight: number
  isAnimating: boolean
  onAnimationComplete: () => void
  stepCount: number
  stepRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
  steps: MultiStepStep[]
}>
const StepCarousel: React.FC<StepCarouselProps> = ({
  bodyProps,
  currentStep,
  currentStepHeight,
  isAnimating,
  onAnimationComplete,
  stepCount,
  stepRefs,
  steps,
}) => {
  // This is used as an inferior alternative to the `reducedMotion` prop added to `<MotionConfig>` in newer versions of
  // `framer-motion`.
  const { prefersReducedMotion } = useUserEnvironment()
  return (
    <StrictMode>
      {/* The carousel wrapper provides the 'viewport' for the carousel, setting up its size and margins. */}
      <div className={styles.classes.multiStepCarouselWrapper}>
        {/* The carousel itself is a sliding filmstrip that resizes to the `block-size` of each step and slides back
      and forth on the X axis. */}
        <motion.div
          animate={
            prefersReducedMotion
              ? { blockSize: `${currentStepHeight}px` }
              : {
                  blockSize: `${currentStepHeight}px`,
                  x: `calc(-${currentStep} * 100% / ${stepCount})`,
                }
          }
          aria-live="polite"
          className={styles.classes.multiStepCarousel}
          data-animating={isAnimating}
          // NOTE: this produces a warning about trying to animate from auto to a pixel value, but the code works fine
          // in practice. An initial `block-size` *must* be set; otherwise on initial render the carousel will default
          // to the `block-size` of the largest step and animate to the size of the first step. Setting it to
          // `stepHeights[0]` seems to have the same resize-on-render issue; `"auto"` avoids the initial resize.
          initial={{ blockSize: 'auto' }}
          onAnimationComplete={onAnimationComplete}
          style={
            {
              '--step-count': stepCount,
            } as React.CSSProperties
          }
          transition={{
            ...FramerMotionTransition(),
            type: prefersReducedMotion ? false : 'tween',
          }}
        >
          {/* Each step is rendered as a separate `<Body>` */}
          {steps.map(({ bodyContent, title }, index) => (
            <Tabs.Content
              asChild
              // All `<Tabs.Content>`s (steps) need to be rendered or the sliding animation between steps doesn't work
              // correctly.
              forceMount
              key={index}
              value={index.toString()}
            >
              <Body ref={(el) => (stepRefs.current[index] = el)} {...bodyProps}>
                <h3 className={styles.classes.multiStepStepTitle}>{title}</h3>
                {bodyContent}
              </Body>
            </Tabs.Content>
          ))}
        </motion.div>
      </div>
    </StrictMode>
  )
}
StepCarousel.displayName = 'Lapidary: StepCarousel ← MultiStep ← Modal'

type StepFooterProps = Readonly<{
  currentStep: number
  footerContent: ReactNode
  onNavigate: (step: number) => void
  stepCount: number
  nextDisabled?: boolean
}>
const StepFooter: React.FC<StepFooterProps> = ({
  currentStep,
  footerContent,
  onNavigate,
  stepCount,
  nextDisabled = false,
}) => (
  <StrictMode>
    <Footer
      aria-live="polite"
      className={styles.classes.multiStepFooter}
      css={styles.multiStepFooter}
    >
      <Button
        buttonText="Back"
        className={styles.classes.multiStepPreviousStepButton}
        disabled={currentStep === 0}
        onClick={() => onNavigate(currentStep - 1)}
        name="Previous step"
        variant="secondary"
      />
      {currentStep < stepCount - 1 ? (
        <Button
          buttonText="Next"
          disabled={nextDisabled}
          onClick={() => onNavigate(currentStep + 1)}
          name="Next step"
          variant="primary"
        />
      ) : (
        footerContent
      )}
    </Footer>
  </StrictMode>
)
StepFooter.displayName = 'Lapidary: StepFooter ← MultiStep ← Modal'

/**
 * Specialized variation of Modal for rendering multiple 'steps' as a carousel.
 *
 * Each step is rendered as a `<Body>`, and a `<Footer>` is automatically added with next/previous buttons. When the
 * user reaches the final step `footerContent` will replace the next-step button.
 */
export type MultiStepProps = BodyProps &
  Readonly<{
    /** Accessibility-only `<Description>`. */
    description: string
    /**
     * Content to render in place of the ‘Next’ button when the user is on the final step--generally a submit button.
     */
    finalStepFooterContent: ReactNode
    steps: MultiStepStep[]
  }>
export const MultiStep: React.FC<MultiStepProps> = ({
  description,
  finalStepFooterContent,
  steps,
  ...props
}) => {
  const stepCount = steps.length
  if (stepCount < 2)
    throw new Error('<MultiStep> must be given at least two steps')

  const theme = useTheme()
  const {
    currentStep,
    currentStepHeight,
    handleTabValueChange,
    isAnimating,
    navigateToStep,
    setIsAnimating,
    stepRefs,
  } = useMultiStepState({ stepCount, steps })

  return (
    <StrictMode>
      <Tabs.Root
        className={styles.classes.multiStepCarouselWrapper}
        css={[styles.multiStep, styles.multiStepDynamic(theme)]}
        onValueChange={handleTabValueChange}
        value={currentStep.toString()}
      >
        <StepProgressBar
          currentStep={currentStep}
          stepCount={stepCount}
          steps={steps}
        />
        <VisuallyHidden asChild>{description}</VisuallyHidden>
        <StepCarousel
          bodyProps={props}
          currentStep={currentStep}
          currentStepHeight={currentStepHeight}
          isAnimating={isAnimating}
          onAnimationComplete={() => setIsAnimating(false)}
          stepCount={stepCount}
          stepRefs={stepRefs}
          steps={steps}
        />
      </Tabs.Root>
      <StepFooter
        currentStep={currentStep}
        footerContent={finalStepFooterContent}
        nextDisabled={steps[currentStep]?.nextDisabled}
        onNavigate={navigateToStep}
        stepCount={stepCount}
      />
    </StrictMode>
  )
}
MultiStep.displayName = 'Lapidary: MultiStep ← Modal'
